import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Grid } from "@/comn/components";
import { utils, comnUtils, comnEnvs } from "@/comn/utils";
import { Page, Group, Layout, Button, FormControl } from "@/comn/components";
import { useFetch, useForm, useGrid, useStore, useToast, useResource, useModal, usePage, usePopup } from "@/comn/hooks";
import { APIS, SF_XTRN_USR_INFO, URLS } from "@/tra/tancis/ptli/usr/xtrnusr/services/PtliXtrnUsrService";

export const PTLI0922003S = (): JSX.Element => {
    const { pgeUid, menuId } = usePage({
        auth: ["auth_ptli0001", "auth_ptli0015", "auth_ptlianyADM"],
    });
    const { t } = useTranslation();
    const navigate = useNavigate();
    const modal = useModal();
    const { openPopup } = usePopup();
    const { pgeStore, setStore, getStore } = useStore({ pgeUid: pgeUid });
    const toast = useToast();
    const { xtrnUsrUuid } = useParams();
    const [rtrnYn, setRtrnYn] = useState("N");
    const [frstUsrStatCd, setFrstUsrStatCd] = useState();
    const [isDeleted, setIsDeleted] = useState(false); // Track if user is deleted !== 삭제된 사용자 여부 추적 ==!
    let isFrst = true;

    useResource({
        defaultSchema: [
            { area: "comnCd", comnCd: "COM0021" },
            { area: "comnCd", comnCd: "PTL0001" },
            { area: "cntyCd" },
        ],
    });

    const form = {
        xtrnUsrInfo: useForm({
            defaultSchema: SF_XTRN_USR_INFO,
        }),
    };

    const fetch = {
        getXtrnUsrInfo: useFetch({
            api: () => APIS.getXtrnUsrInfo(xtrnUsrUuid),
            enabled: !!xtrnUsrUuid,
            onSuccess: (data) => {
                const xtrnUsrInfo = data.xtrnUsrInfo.content;
                form.xtrnUsrInfo.setValues(xtrnUsrInfo);
                // Set deleted state based on delYn !== delYn 기반으로 삭제 상태 설정 ==!
                setIsDeleted(xtrnUsrInfo.delYn === "Y");

                if (isFrst) {
                    isFrst = false;
                    setFrstUsrStatCd(xtrnUsrInfo.usrStatCd);
                }
            },
            onError: (error) => {},
            showToast: true,
        }),

        deleteXtrnUsrInfo: useFetch({
            api: (xtrnUsrUuid) => APIS.deleteXtrnUsrInfo(xtrnUsrUuid),
            onSuccess: () => {
                modal.openModal({
                    content: "msg.00003",
                    onCancel: () => {
                        navigate(`${URLS.ptli0922001q}?menuId=${menuId}`);
                    },
                });
            },
            onError: (error) => {},
            showToast: true,
        }),

        // Revert (restore) deleted user !== 삭제된 사용자 복구 ==!
        revertXtrnUsrInfo: useFetch({
            api: (xtrnUsrUuid) => APIS.revertXtrnUsrInfo(xtrnUsrUuid),
            onSuccess: () => {
                modal.openModal({
                    content: "msg.com.00059", // Successfully updated !== 정상적으로 수정되었습니다 ==!
                    onCancel: () => {
                        navigate(`${URLS.ptli0922001q}?menuId=${menuId}`);
                    },
                });
            },
            onError: () => toast.showToast({ type: "error", content: "msg.com.00022" }),
            showToast: true,
        }),

        updateXtrnUsrInfo: useFetch({
            api: (info: any) => APIS.updateXtrnUsrInfo(info),
            enabled: false,
            onSuccess: () => {
                modal.openModal({ content: "msg.com.00059" });
                navigate(`${URLS.ptli0922001q}?menuId=${menuId}`);
            },
            onError: () => toast.showToast({ type: "error", content: "msg.com.00022" }),
        }),

        updateXtrnUsrPwdInit: useFetch({
            api: (data) => APIS.updateXtrnUsrPwdInit(data),
            onSuccess: (data) => {
                modal.openModal({ content: "msg.ptl.00764" });
            },
            onError: (error) => {
                const data = error.response.data;
                modal.openModal({ content: data.errorMessage });
            },
        }),
    };

    const setRequired = (value: Boolean) => {
        form.xtrnUsrInfo.setSchemas(
            ["xtrnUsrIdntfNo", "usrNm", "frgnrYn", "aplcCntyCd", "usrMoblNo", "usrEmlAddr", "rsdtAddr"],
            { required: value },
        );
    };

    const handler = {
        onDeleteBtnClick: () => {
            modal.openModal({
                content: "msg.00103",
                onConfirm: () => {
                    fetch.deleteXtrnUsrInfo.fetch(xtrnUsrUuid);
                },
            });
        },

        // Revert deleted user !== 삭제된 사용자 복구 ==!
        onRevertBtnClick: () => {
            modal.openModal({
                content: "msg.com.00050", // Confirm update? !== 수정하시겠습니까? ==!
                onConfirm: () => {
                    fetch.revertXtrnUsrInfo.fetch(xtrnUsrUuid);
                },
            });
        },

        onUpdateBtnClick: () => {
            const curUsrStatCd = form.xtrnUsrInfo.getValue("usrStatCd");
            if (frstUsrStatCd !== curUsrStatCd) {
                form.xtrnUsrInfo.clearErrors();

                if (curUsrStatCd == 2) {
                    if (comnUtils.isEmpty(form.xtrnUsrInfo.getValue("mdfyRsn"))) {
                        form.xtrnUsrInfo.setError("mdfyRsn", {
                            message: "msg.com.00005",
                            type: "error",
                        });
                        return;
                    }
                }

                modal.openModal({
                    content: "msg.com.00050",
                    onConfirm: () => {
                        setRequired(false);
                        form.xtrnUsrInfo.handleSubmit(
                            () => {
                                const data = form.xtrnUsrInfo.getValues();
                                fetch.updateXtrnUsrInfo.fetch(data);
                            },
                            () => {
                                toast.showToast({ type: "warning", content: "msg.00002" });
                            },
                        )();
                    },
                });
            } else {
                setRequired(true);

                if (!comnUtils.isEmpty(form.xtrnUsrInfo.errors)) {
                    toast.showToast({ type: "warning", content: "msg.00002" });
                    return;
                }

                form.xtrnUsrInfo.handleSubmit(
                    () => {
                        modal.openModal({
                            content: "msg.com.00050",
                            onConfirm: () => {
                                const data = form.xtrnUsrInfo.getValues();
                                fetch.updateXtrnUsrInfo.fetch(data);
                            },
                        });
                    },
                    () => {
                        toast.showToast({ type: "warning", content: "msg.00002" });
                    },
                )();
            }
        },

        onResetBtnClick: () => {
            modal.openModal({
                content: "msg.ptl.00749",
                onConfirm: () => {
                    if (!comnUtils.isEmpty(form.xtrnUsrInfo.errors)) {
                        toast.showToast({ type: "warning", content: "msg.00002" });
                        return;
                    }
                    form.xtrnUsrInfo.handleSubmit(
                        () => {
                            const usrAplyInfo = form.xtrnUsrInfo.getValues();
                            fetch.updateXtrnUsrPwdInit.fetch(usrAplyInfo);
                        },
                        () => {
                            toast.showToast({ type: "warning", content: "msg.00002" });
                        },
                    )();
                },
            });
        },

        onUsrStatChange: (value: any) => {
            form.xtrnUsrInfo.clearErrors();
            if (value == 2) {
                form.xtrnUsrInfo.setSchema("mdfyRsn", { required: true });
            } else {
                form.xtrnUsrInfo.setSchema("mdfyRsn", { required: false });
            }
        },
    };

    useEffect(() => {
        fetch.getXtrnUsrInfo.fetch();
        form.xtrnUsrInfo.setEditable("xtrnUsrIdntfNo", false);
        form.xtrnUsrInfo.setEditable("usrNm", false);
    }, []);

    return (
        <Page>
            <form>
                <Group>
                    <Group.Header>
                        <Group.Title title={t("T_USR_INFO")} />
                    </Group.Header>
                    <Group.Body>
                        <Group.Section>
                            <Group.Row>
                                <Group.Control {...form.xtrnUsrInfo.schema.xtrnUsrIdntfNo} />
                                <Group.Control {...form.xtrnUsrInfo.schema.usrNm} />
                            </Group.Row>
                            <Group.Row>
                                <Group.Control {...form.xtrnUsrInfo.schema.frgnrYn} />
                                <Group.Control {...form.xtrnUsrInfo.schema.aplcCntyCd} />
                            </Group.Row>
                            <Group.Row>
                                <Group.Control {...form.xtrnUsrInfo.schema.usrMoblNo} />
                                <Group.Control {...form.xtrnUsrInfo.schema.bidrYn} />
                            </Group.Row>
                            <Group.Row>
                                <Group.Control {...form.xtrnUsrInfo.schema.usrEmlAddr} />
                                <Group.Control
                                    {...form.xtrnUsrInfo.schema.usrStatCd}
                                    onChange={handler.onUsrStatChange}
                                />
                            </Group.Row>
                            <Group.Row>
                                <Group.Control {...form.xtrnUsrInfo.schema.rsdtAddr} />
                            </Group.Row>
                            <Group.Row>
                                <Group.Control {...form.xtrnUsrInfo.schema.mdfyRsn} />
                            </Group.Row>
                        </Group.Section>
                    </Group.Body>
                </Group>
            </form>
            <Layout direction="row">
                <Layout.Left>
                    <Button role="list" onClick={() => navigate(URLS.ptli0922001q)} />
                </Layout.Left>
                <Layout.Right>
                    <Button color="warning" onClick={handler.onResetBtnClick}>
                        {t("B_RESET_PWD")}
                    </Button>
                    {/* Show Revert button if deleted, Delete button if not !== 삭제된 경우 복구 버튼, 아닌 경우 삭제 버튼 표시 ==! */}
                    {isDeleted ? (
                        <Button color="success" onClick={handler.onRevertBtnClick}>
                            {t("B_REVERT_USR")}
                        </Button>
                    ) : (
                        <Button role="delete" onClick={handler.onDeleteBtnClick} />
                    )}
                    <Button role="save" onClick={handler.onUpdateBtnClick} />
                </Layout.Right>
            </Layout>
        </Page>
    );
};